import React, { useEffect, useMemo, useState } from 'react'
import { BugIcon, CheckIcon, LifeBuoyIcon, LightbulbIcon, UsersIcon, XIcon } from 'lucide-react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'

import { Button, FieldLabel, Input, Skeleton } from '@repo/ui'
import { FileUploadField, InfoTooltip, StatusChip } from 'components'
import { useSnackbar } from 'contexts'
import { useDebouncedValue } from 'hooks'
import { useCreateSupportRequest, useSimilarRequests } from 'hooks/api'
import { BOARD_ITEM_STATUSES } from 'constants/statuses'
import { getBuildContext } from 'utils'
import { autoReportIdFor } from 'utils/errorReporter'
import {
  attachedContextCopy,
  clearDraft,
  clearErrorHandoff,
  readDraft,
  saveDraft,
  type ErrorHandoff,
} from 'utils/supportHandoff'
import { type SimilarSupportRequest, type SupportRequestType } from 'services/supportApi'

import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_COUNT,
  ATTACHMENT_MAX_SIZE_MB,
  uploadAttachments,
} from '../attachments'
import { MESSAGE_FIELD_SX } from '../messageField'

/**
 * THE support intake form (VRG-226 — C2). One component, one rendering.
 *
 * There is deliberately no second copy: the modal below is the only presentation
 * today, and any future full-page presentation renders THESE fields with THIS
 * hook rather than restating them. Two renderings of one form always diverge —
 * this repo has already paid for that twice (the docs/settings label drift, and
 * three hand-rolled payment-method previews). The split is state-in-a-hook /
 * fields-in-a-component precisely so a container can own its own chrome (a modal
 * footer, a page's action bar) without owning a second copy of the fields.
 *
 * JSM-lite: a TYPE, ONE text field, an attachment. The restraint is the feature.
 * Every extra field is a reason not to file, and everything a longer form would
 * ask for is either already known (which company, which user, which browser,
 * which page — all captured elsewhere) or better asked in the thread once
 * someone has read the request. Resist adding a subject, a priority, a category,
 * a "steps to reproduce": the epic's stated position is that a long form is the
 * thing we are deliberately not building.
 */

export const SUPPORT_REQUEST_TYPE_OPTIONS: {
  value: SupportRequestType
  label: string
  hint: string
  icon: React.ReactNode
}[] = [
  {
    value: 'support',
    label: 'Contact support',
    hint: 'Something is not working the way you expect, or you need a hand.',
    icon: <LifeBuoyIcon strokeWidth={1} size={20} />,
  },
  {
    value: 'bug',
    label: 'Report a bug',
    hint: 'Something is broken. Tell us what you did and what happened.',
    icon: <BugIcon strokeWidth={1} size={20} />,
  },
  {
    value: 'feature',
    label: 'Suggest a feature',
    // Was "It goes to your company's feature board, where colleagues can upvote
    // it." That board is gone (VRG-324): it required visiting a surface in order
    // to rank things you had already asked for. The +1 now happens HERE, when a
    // colleague types the same thing — so the hint describes what actually
    // happens rather than pointing at a page that no longer exists.
    hint: "Something is missing. If a colleague has already asked for it, you'll be offered the chance to add yours to theirs.",
    icon: <LightbulbIcon strokeWidth={1} size={20} />,
  },
]

export const isSupportRequestType = (
  value: string | null | undefined,
): value is SupportRequestType =>
  value === 'support' || value === 'bug' || value === 'feature'

/*
  The accept map and the two caps moved to `../attachments`, which the reply composer
  now shares (§36 — the reply box is the SECOND surface that attaches files, so they
  stop being this form's constants). Everything about why each format is on the list,
  and why SVG is not, lives there.
*/

export interface SupportRequestFormApi {
  type: SupportRequestType
  setType: (next: SupportRequestType) => void
  body: string
  setBody: (next: string) => void
  /** The chosen files, not URLs. Uploaded on submit — see `submit`. */
  attachments: File[]
  setAttachments: (next: File[]) => void
  canSubmit: boolean
  /** True from the moment submit is pressed until the request exists. */
  isPending: boolean
  /** True only during the attachment upload leg, so the button can say so. */
  isUploading: boolean
  /**
   * Whether a page URL was actually captured — i.e. whether the "we already capture the page you
   * came from" line is TRUE right now.
   *
   * It is not always. Verified against the database: opened over a page (the designed path, via
   * the Help popover) the row records that page — #1013 stored `/products`. But on a COLD load of
   * /support/new (an emailed link, a refresh, a pasted URL) there is no page underneath, and the
   * row stored either the intake's own URL or NULL. Claiming to have captured something we did
   * not is the one thing this reassurance must not do, so the copy is conditional on this.
   */
  hasPageContext: boolean
  /**
   * VRG-721 — the error toast this request was opened from, or null. Rendered
   * read-only ("Error we'll attach") and sent as `clientContext.error`.
   */
  errorHandoff: ErrorHandoff | null
  /**
   * "Add mine to this one" — the colleague's request this one will be attached to,
   * or null (VRG-324).
   *
   * The whole object, not just an id, because the FIELDS need to render what was
   * chosen ("you're adding to #1013") after the suggestion list has been replaced
   * by further typing.
   */
  linkTo: SimilarSupportRequest | null
  setLinkTo: (next: SimilarSupportRequest | null) => void
  /** Candidates for what is currently typed. Empty unless the type is `feature`. */
  similar: SimilarSupportRequest[]
  isSearchingSimilar: boolean
  submit: () => void
}

interface UseSupportRequestFormArgs {
  /** Preselected type, from `?type=` on the intake URL. */
  initialType: SupportRequestType
  /**
   * Prefilled body, from `?body=` on the intake URL (VRG-227 — see
   * `SupportIntakeRoute`'s `initialBody` docblock). Empty for every entry point except
   * Ask Vera's "no data for this" card, which seeds this with the user's original
   * question so they don't have to retype it.
   */
  initialBody: string
  /**
   * The page the user was looking at when they opened the intake. As a modal
   * this is knowable and correct — the whole reason the intake is a modal is
   * that you report a bug ABOUT the page you are on, so the page is still
   * mounted behind it. (As a full page it was only ever `document.referrer`.)
   */
  pageUrl: string | null
  /** Called with the created request's id once the server has accepted it. */
  onCreated: (id: string) => void
  /** VRG-721 — whether `initialType` came from the URL (it then beats a draft's type). */
  hasExplicitType?: boolean
  /** VRG-721 — the error toast this intake was opened from. */
  errorHandoff?: ErrorHandoff | null
  /** VRG-721 — sessionStorage key for this page's draft. Null disables drafts. */
  draftKey?: string | null
}

export const useSupportRequestForm = ({
  initialType,
  initialBody,
  pageUrl,
  onCreated,
  hasExplicitType = true,
  errorHandoff = null,
  draftKey = null,
}: UseSupportRequestFormArgs): SupportRequestFormApi => {
  const { open: notification } = useSnackbar()

  /*
    VRG-721 — DRAFTS. The modal unmounts on close (Layout mounts it per open, so
    the form starts clean), which used to mean a half-written report vanished the
    moment you clicked away to check something. The draft lives in sessionStorage,
    keyed by the page behind the modal, and is read ONCE, at mount.

    Precedence: a URL that names a type or a body wins over the draft, because it is
    a fresh instruction ("Report a bug", Ask Vera's prefilled question); the draft
    fills in only what the URL left unsaid.
  */
  const draft = useMemo(() => (draftKey ? readDraft(draftKey) : null), [draftKey])

  const [type, setType] = useState<SupportRequestType>(
    hasExplicitType ? initialType : draft?.type ?? initialType,
  )
  const [body, setBody] = useState(initialBody || draft?.body || '')
  const [attachments, setAttachments] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [linkTo, setLinkTo] = useState<SimilarSupportRequest | null>(null)

  /**
   * DEDUPE AT INTAKE (VRG-324) — search the caller's OWN company as they type.
   *
   * 400ms, not 150: the point is to react to a finished thought, not to a
   * keystroke. Suggestions that reshuffle mid-word are noise beside a textarea
   * someone is composing in, and each tick is a round trip.
   *
   * Feature type only. A bug is deduped by a human triaging the queue, and the
   * mechanism this rides on (`board_item_id`) is refused for non-feature requests
   * server-side — so searching on a bug would offer an option that cannot be taken.
   */
  // Destructured: the hook returns `[value, cancel] as const`, not a bare value.
  const [debouncedBody] = useDebouncedValue(body, 400)
  const { data: similar = [], isFetching: isSearchingSimilar } = useSimilarRequests(
    debouncedBody,
    type === 'feature',
  )

  /*
    A chosen link cannot outlive the conditions that made it offerable.

    Switching away from `feature` (the type cards stay live while composing) would
    otherwise submit a bug carrying a `linkToRequestId`, which the API rejects —
    turning a type change into a failed submission with no visible cause. Cleared
    here rather than at submit so the "you're adding to #N" panel disappears at the
    moment the user makes it untrue.
  */
  useEffect(() => {
    if (type !== 'feature') setLinkTo(null)
  }, [type])

  // The URL is the source of truth for the type while the intake is open: hitting
  // "Report a bug" in the Help popover with the intake ALREADY open changes the
  // query string, and the selection has to follow it. Only the selection — the
  // body the user has typed survives, which is why this is an effect on
  // `initialType` rather than remounting the form on every URL change.
  useEffect(() => {
    if (hasExplicitType) setType(initialType)
  }, [initialType, hasExplicitType])

  // Same reasoning, for the prefilled body (VRG-227): `initialBody` only ever changes
  // when the intake URL's `?body=` param itself changes — i.e. the intake was
  // (re)triggered from a different entry point — never merely because the user is
  // typing, since typing doesn't touch the URL. So this can't clobber in-progress edits;
  // it only re-seeds the field when the intake is genuinely reopened for something new.
  //
  // VRG-721: only a NON-EMPTY `?body=` re-seeds. This effect also runs on mount, and
  // seeding '' there would wipe the draft that was just restored.
  useEffect(() => {
    if (initialBody) setBody(initialBody)
  }, [initialBody])

  // Saved on every change; an empty body removes the draft (see `saveDraft`).
  useEffect(() => {
    if (draftKey) saveDraft(draftKey, { type, body })
  }, [draftKey, type, body])

  const { mutate, isPending } = useCreateSupportRequest()

  const canSubmit = body.trim().length > 0 && !isPending && !isUploading

  /**
   * Upload on SUBMIT, not on select. Two things decided this:
   *
   *  1. ORPHANS. This intake is a modal opened over whatever page you were
   *     already on, from a Help popover, for a "file it and get back to work"
   *     flow — abandoning it half-typed is the normal case, not the edge one.
   *     Uploading on select writes a blob to the bucket for every abandoned
   *     draft, owned by no row, and there is no reaper job in this repo to sweep
   *     them. On-submit cannot orphan: nothing is stored unless a request is
   *     about to exist to point at it.
   *  2. The wait it costs is small and already visible. A screenshot is a couple
   *     of MB, so the extra leg is roughly a second, and the button says
   *     "Uploading…" then "Submitting…" rather than sitting silent.
   *
   * What on-select would have bought is a progress bar and a slightly snappier
   * Submit. Both are worth less than not littering the bucket.
   */
  const submit = async () => {
    if (!canSubmit) return

    let attachmentUrls: string[] = []

    if (attachments.length > 0) {
      setIsUploading(true)
      try {
        // Concurrency and partial-success are `uploadAttachments`' rules now, shared with
        // the reply composer — see `../attachments` for why each one is what it is.
        const result = await uploadAttachments(attachments)
        attachmentUrls = result.urls

        // A FAILED UPLOAD MUST NOT EAT THE REQUEST. The files are the optional part; the
        // paragraph the user just wrote is the whole point, and throwing it away because an
        // image did not make it — after they pressed the button that says Submit — is the
        // single worst outcome this form has. So we say plainly what did not happen and
        // send the rest.
        //
        // The alternative considered was a mid-submit confirm ("attach failed, send
        // anyway?"). Rejected: it stops the flow to ask a question with one sensible
        // answer, and a user who has already committed to submitting is the wrong person
        // to interrupt.
        if (result.failed.length > 0) {
          notification({
            type: 'error',
            title:
              result.failed.length === attachments.length
                ? `Couldn't attach ${result.failed.join(', ')}. Your request was sent without ${
                    result.failed.length === 1 ? 'it' : 'them'
                  }.`
                : `Couldn't attach ${result.failed.join(', ')}. Your request was sent with the rest.`,
          })
        }
      } finally {
        setIsUploading(false)
      }
    }

    /*
      VRG-378: read at SUBMIT time, not at mount.

      The values themselves do not change while a modal is open, but reading them
      here keeps the capture in the same place as the send — so a future field that
      IS time-varying (an active certificate, a live error count) cannot silently
      record the wrong moment, and there is one obvious place to look for "what do
      we actually attach".
    */
    const build = getBuildContext()

    /*
      VRG-721 — the error this request is about, and the automatic report the same
      error already filed. The report id is awaited briefly, not indefinitely: if the
      quiet report is still in flight the server links them by signature instead
      (`resolveAutoReportLink`), so a slow network costs nothing but a lookup.
    */
    let autoReportId: string | undefined
    if (errorHandoff?.signatureKey) {
      autoReportId = await Promise.race([
        autoReportIdFor(errorHandoff.signatureKey),
        new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), 1500)),
      ])
    }
    const clientContext = errorHandoff
      ? {
          ...build.clientContext,
          error: errorHandoff.error,
          ...(autoReportId ? { autoReportId } : {}),
        }
      : build.clientContext

    mutate(
      {
        type,
        body: body.trim(),
        attachmentUrls,
        pageUrl,
        // Sent only when the user actually took the offer. `linkTo` is already
        // cleared on a type change, so this cannot carry a stale id.
        linkToRequestId: linkTo?.requestId ?? null,
        environment: build.environment,
        appVersion: build.appVersion,
        clientContext,
      },
      {
        onSuccess: created => {
          // Sent, so there is nothing left to restore — and the handoff is spent.
          if (draftKey) clearDraft(draftKey)
          if (errorHandoff) clearErrorHandoff(errorHandoff.id)
          notification({
            type: 'success',
            title: linkTo
              ? // Says what the link DID. "Sent" alone would leave the user unsure
                // whether adding to the colleague's request had any effect, which
                // is the one thing they deliberately chose to do.
                `Added to request #${linkTo.reference}. We'll reply in your thread.`
              : `Request #${created.reference} sent. We'll reply in this thread.`,
          })
          onCreated(created.id)
        },
        onError: (error: any) => {
          notification({
            type: 'error',
            title: error?.message || "Couldn't send your request. Please try again.",
          })
        },
      },
    )
  }

  return {
    type,
    setType,
    body,
    setBody,
    attachments,
    setAttachments,
    canSubmit,
    // One flag for the caller's disabled state: the button must stay dead for
    // BOTH legs, and a container should not have to remember there are two.
    isPending: isPending || isUploading,
    isUploading,
    hasPageContext: Boolean(pageUrl),
    errorHandoff,
    linkTo,
    setLinkTo,
    similar,
    isSearchingSimilar,
    submit,
  }
}

/**
 * "Someone at your company already asked for this" (VRG-324).
 *
 * ## Why this exists at all
 *
 * It replaces a whole page. The feature board was a destination you had to visit
 * in order to rank things you had already asked for, so almost nobody did, and the
 * votes it did collect were intra-company and therefore not comparable across
 * customers. The +1 moved here — to the one moment someone is provably paying
 * attention to this exact idea, because they are in the middle of typing it.
 *
 * ## The disclosure, stated plainly
 *
 * This shows a member the BODY of a colleague's request, including a member with
 * no permission to browse the company's requests. That is deliberate and is the
 * design's stated position: no browsing surface, contextual disclosure at the
 * moment of duplication, scoped to the caller's own company. It never crosses
 * companies — the API takes the scope off the authenticated caller, and a
 * two-company fixture proves it.
 *
 * Names are NOT shown, though the API could. "Someone at your company" is enough
 * to make the decision ("is this the same idea?"); who asked is a second, wider
 * disclosure that buys nothing here.
 *
 * TODO(VRG-326-Support.Dedupe.Suggestions.Shared.Component / TD-055): this is the
 * SECOND search-as-you-type dedupe flow in the app — `SuggestIntegrationModal`
 * has one too — so §36 says extract a shared component. Deferred deliberately:
 * the two differ in data shape, matching semantics and what taking the offer
 * does, and the integrations flow (with its 409 "vote instead" path) is outside
 * this task's scope to refactor unverified. See the task for the full reasoning.
 */
const SimilarSuggestions = ({ form }: { form: SupportRequestFormApi }) => {
  const theme = useTheme()

  // Once chosen, the list is replaced by the choice. Leaving the other candidates
  // on screen invites a second click that would silently switch the target.
  if (form.linkTo) {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.brand[500]}`,
          backgroundColor: alpha(theme.palette.brand[500], 0.1),
        }}
      >
        <Stack direction="row" spacing={3} alignItems="flex-start">
          <CheckIcon size={16} strokeWidth={1} color={theme.palette.brand[600]} />
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="body3">
              Adding yours to request #{form.linkTo.reference}
            </Typography>
            <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              You still get your own request and your own thread. Both count towards
              the same idea, which is how we see how many people want it.
            </Typography>
          </Stack>
          {/*
            X, not a trash can (§38): this un-picks a choice. Nothing is deleted —
            the colleague's request is untouched and no row disappears.
          */}
          <Button
            variant="secondary"
            disableElevation
            onClick={() => form.setLinkTo(null)}
            aria-label="Don't add to that request"
            sx={{ minWidth: 0, px: 2 }}
          >
            <XIcon size={14} strokeWidth={1.5} />
          </Button>
        </Stack>
      </Box>
    )
  }

  // Skeleton, not a spinner (§19), and only on the FIRST search — subsequent
  // keystrokes keep the previous suggestions on screen (`placeholderData` in
  // `useSimilarRequests`) rather than flashing a placeholder over a good answer.
  if (form.isSearchingSimilar && form.similar.length === 0) {
    return (
      <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.primary[100]}` }}>
        <Stack spacing={2}>
          <Skeleton width={220} height={16} />
          <Skeleton lines={2} />
        </Stack>
      </Box>
    )
  }

  // Nothing similar is NOT a state worth rendering. An empty panel saying "no
  // matches" under a half-typed sentence is a report on a search the user never
  // asked to run.
  if (form.similar.length === 0) return null

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <UsersIcon size={14} strokeWidth={1.5} />
        <Typography variant="body4" color="text.secondary">
          {form.similar.length === 1
            ? 'Someone at your company already asked for something like this'
            : 'People at your company already asked for things like this'}
        </Typography>
      </Stack>

      {form.similar.map((candidate: SimilarSupportRequest) => (
        <Box
          key={candidate.requestId}
          role="button"
          tabIndex={0}
          onClick={() => form.setLinkTo(candidate)}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            form.setLinkTo(candidate)
          }}
          sx={{
            p: 3,
            borderRadius: 2,
            cursor: 'pointer',
            outline: 'none',
            border: `1px solid ${theme.palette.primary[100]}`,
            // Action row, not a selectable card (§34): clicking it commits the
            // choice and swaps this whole list for the confirmation above, so
            // there is no persistent "selected" state for a tint to mean.
            '&:hover, &:focus-visible': {
              borderColor: theme.palette.brand[600],
              boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
            },
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body4" color="text.secondary">
                #{candidate.reference}
              </Typography>
              {/* Board status when it has one — the same pill Company → Support
                  shows on a promoted row, so the two surfaces say the same thing
                  about the same idea. */}
              {candidate.boardItemStatus && candidate.boardItemStatus !== 'merged' && (
                <StatusChip
                  statuses={BOARD_ITEM_STATUSES}
                  status={candidate.boardItemStatus}
                  size="small"
                />
              )}
              <Box sx={{ flex: 1 }} />
              {candidate.requestCount > 1 && (
                <Typography variant="body4" color="text.secondary">
                  {candidate.requestCount} people asked
                </Typography>
              )}
            </Stack>

            <Typography
              variant="body3"
              sx={{
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {candidate.body}
            </Typography>

            <Typography variant="body4" sx={{ color: theme.palette.brand[600] }}>
              Add mine to this
            </Typography>
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

/**
 * VRG-721 — "Error we'll attach": what the user just saw, shown back to them
 * read-only before they write a word.
 *
 * Two jobs. It tells the reader they do not need to describe the error message
 * (we have it, verbatim), which is the part people get wrong when retyping; and it
 * shows exactly what is being sent on their behalf — the same rule the captured-
 * context block on a request already follows. The request line and ref are the
 * facts a staffer will search on; the user can quote the ref on a call.
 */
const AttachedErrorSummary = ({ handoff }: { handoff: ErrorHandoff }) => {
  const theme = useTheme()
  const error = handoff.error
  const shown = [error.title, error.text].filter(Boolean).join(' ')
  const request = [
    [error.method, error.path].filter(Boolean).join(' '),
    error.status !== null ? String(error.status) : error.kind === 'network' ? 'no response' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  let time: string | null = null
  try {
    time = new Date(error.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    time = null
  }

  return (
    <Box
      data-testid="support-attached-error"
      sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.primary[100]}` }}
    >
      <Stack spacing={2}>
        <Typography variant="body4" color="text.secondary">
          Error we'll attach
        </Typography>
        {shown && (
          <Typography variant="body3" sx={{ lineHeight: 1.5, overflowWrap: 'anywhere' }}>
            {shown}
          </Typography>
        )}
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {request && (
            <Typography variant="body4" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {request}
            </Typography>
          )}
          {error.ref && (
            <Typography
              variant="body4"
              color="text.secondary"
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              Ref
              <InfoTooltip term="support-error-reference" />
              <Box component="span" sx={{ ml: 1, fontFamily: 'monospace' }}>
                {error.ref}
              </Box>
            </Typography>
          )}
          {time && (
            <Typography variant="body4" color="text.secondary">
              {time}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

export const SupportRequestFormFields = ({ form }: { form: SupportRequestFormApi }) => {
  const theme = useTheme()
  const selected = SUPPORT_REQUEST_TYPE_OPTIONS.find(option => option.value === form.type)!

  return (
    <Stack spacing={5}>
      <Stack spacing={3}>
        {/*
          The definition sits HERE rather than on the badge that renders the answer. This is
          the one moment the distinction costs the reader anything: they are choosing, the
          choice is permanent (only Vera moves a request's status; nobody moves its type),
          and the badge downstream is read by someone who already knows which one they
          picked. Putting it on the badge would also have put it on the filter drawer, where
          the same component is a toggle and a tooltip would fight the control.
        */}
        <Typography
          variant="body3"
          color="text.secondary"
          sx={{ lineHeight: 1.5, display: 'inline-flex', alignItems: 'center' }}
        >
          What kind of request is this?
          <InfoTooltip term="support-request-type" />
        </Typography>
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
          {SUPPORT_REQUEST_TYPE_OPTIONS.map(option => {
            const active = option.value === form.type
            return (
              <Box
                key={option.value}
                onClick={() => form.setType(option.value)}
                sx={{
                  flex: '1 1 120px',
                  p: 3,
                  borderRadius: 2,
                  cursor: 'pointer',
                  // Selectable card (§34): brand TINT on hover, brand BORDER when
                  // chosen. A border on hover would collide with the selected
                  // state and destroy the distinction.
                  border: `1px solid ${
                    active ? theme.palette.brand[500] : theme.palette.primary[100]
                  }`,
                  backgroundColor: active
                    ? alpha(theme.palette.brand[500], 0.1)
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.brand[500], active ? 0.1 : 0.06),
                  },
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ color: active ? theme.palette.brand[600] : 'text.secondary' }}>
                    {option.icon}
                  </Box>
                  <Typography variant="body3">{option.label}</Typography>
                </Stack>
              </Box>
            )
          })}
        </Stack>
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {selected.hint}
        </Typography>
      </Stack>

      {form.errorHandoff && <AttachedErrorSummary handoff={form.errorHandoff} />}

      {/* THE one text field. */}
      <Stack spacing={3}>
        <Input
          label="What do you need?"
          placeholder="Tell us what happened, or what you'd like to be able to do."
          value={form.body}
          onValueChange={form.setBody}
          multiline
          minRows={5}
          // Same 14px as the reply composer, from the same constant. Both boxes write the
          // same kind of thing and must not end up at two sizes. See `MESSAGE_FIELD_SX`.
          sx={MESSAGE_FIELD_SX}
        />
        {/* Directly under the field it reads from, not at the foot of the form
            (§40): the suggestion has to arrive while the sentence that produced it
            is still in view, or it is a footnote about something already decided. */}
        {form.type === 'feature' && <SimilarSuggestions form={form} />}
      </Stack>

      <Stack spacing={2}>
        {/* The label is rendered HERE rather than via FileUploadField's own
            `label` prop, which uses a bare body2 + 16px gap. Every other field in
            this modal gets its label from FieldLabel (body3, 8px), and two label
            treatments stacked in one 640px modal is exactly the drift §31
            exists to stop. */}
        <FieldLabel label="Attachments" adornment="(optional)" />
        <FileUploadField
          acceptFiles={ATTACHMENT_ACCEPT}
          // Images and PDF first, then video — the common case leads. Kept to one line so the
          // compact dropzone can still fold the size cap onto the end of it.
          description="Images, PDF or video"
          // Plural. One screenshot is rarely the whole story — a bug report usually wants
          // the broken screen AND the console, or a before and an after. The cap is the
          // server's own, imported rather than restated so the picker and the endpoint
          // cannot drift.
          maxFiles={ATTACHMENT_MAX_COUNT}
          maxSizeMb={ATTACHMENT_MAX_SIZE_MB}
          showMaxFileSize
          // Shorter than the 268px default: this sits under three type cards and
          // a five-row textarea, and at full height the dropzone owned the modal.
          // 140 matches the textarea above it (measured 141px), which is what makes
          // the two read as one column rather than the attachments dominating.
          height={140}
          // "Attachments" matches this field's own label; the shared default is "Images".
          galleryTitle="Attachments"
          onFilesChange={files => {
            // FileUploadField hands back its own wrapper ({id, file, …}), not
            // native Files — same unwrap as useProductMutations' buildFormData.
            form.setAttachments(
              (files as unknown as { file: File }[]).map(wrapper => wrapper.file),
            )
          }}
        />
        {/* Only claims what was actually captured. `user_agent` and the account (user + company)
            come off the request server-side and are always present; the PAGE is not, so it is
            named only when we have one. VRG-721: the copy is `attachedContextCopy`, pinned by a
            guard, and no longer ends "so there is nothing else to fill in" — there is: what
            happened, in the box above. */}
        <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {attachedContextCopy({
            hasError: Boolean(form.errorHandoff),
            hasPage: form.hasPageContext,
          })}
        </Typography>
      </Stack>
    </Stack>
  )
}
